import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import CampaignIcon from '@mui/icons-material/Campaign';
import { auth, db } from '../../firebase';
import { useCompanyAnnouncements } from '../../hooks/useCompanyAnnouncements';
import {
  isAnnouncementSeen,
  markAnnouncementAsSeen,
  parseAnnouncementTimestamp,
} from '../../lib/announcements';

/**
 * Blocks the dashboard with unread company broadcasts until acknowledged.
 * @param {{ userData?: { companyId?: string, role?: string } | null }} props
 */
export default function GlobalAnnouncementModal({ userData }) {
  const uid = auth.currentUser?.uid;
  const { announcements, loading } = useCompanyAnnouncements(userData);
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(null);
  const [ackWorking, setAckWorking] = useState(false);

  const unreadQueue = useMemo(() => {
    if (!uid || loading) return [];
    return announcements
      .filter((a) => !isAnnouncementSeen(a, uid))
      .sort((a, b) => {
        const ad = parseAnnouncementTimestamp(a.timestamp ?? a.createdAt)?.getTime() || 0;
        const bd = parseAnnouncementTimestamp(b.timestamp ?? b.createdAt)?.getTime() || 0;
        return bd - ad;
      });
  }, [announcements, loading, uid]);

  useEffect(() => {
    if (loading || !uid) return;
    if (unreadQueue.length === 0) {
      setOpen(false);
      setCurrent(null);
      return;
    }
    setCurrent((prev) => {
      if (prev && unreadQueue.some((a) => a.id === prev.id)) return prev;
      return unreadQueue[0];
    });
    setOpen(true);
  }, [loading, uid, unreadQueue]);

  const handleAcknowledge = useCallback(async () => {
    if (!current?.id || !uid || ackWorking) return;
    setAckWorking(true);
    try {
      await markAnnouncementAsSeen(db, current.id, uid);
      const remaining = unreadQueue.filter((a) => a.id !== current.id);
      if (remaining.length > 0) {
        setCurrent(remaining[0]);
        setOpen(true);
      } else {
        setOpen(false);
        setCurrent(null);
      }
    } catch (e) {
      console.error('GlobalAnnouncementModal acknowledge:', e);
    } finally {
      setAckWorking(false);
    }
  }, [current, uid, ackWorking, unreadQueue]);

  if (!current) return null;

  const when = parseAnnouncementTimestamp(current.timestamp ?? current.createdAt);

  return (
    <Dialog
      open={open}
      onClose={ackWorking ? undefined : handleAcknowledge}
      maxWidth="sm"
      fullWidth
      aria-labelledby="global-announcement-title"
    >
      <DialogTitle id="global-announcement-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CampaignIcon color="primary" />
        {current.title || 'Announcement'}
      </DialogTitle>
      <DialogContent dividers>
        {when && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            {when.toLocaleString()}
            {unreadQueue.length > 1 ? ` · ${unreadQueue.length} unread` : ''}
          </Typography>
        )}
        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
          {current.message || 'No message provided.'}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          variant="contained"
          onClick={handleAcknowledge}
          disabled={ackWorking}
          startIcon={ackWorking ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {ackWorking ? 'Saving…' : 'Acknowledge'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
